import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import { useBotProtection, BotProtectionFields } from '../utils/useBotProtection';
import axios from 'axios';
import { Loader, Mail, MapPin, Phone, Send } from 'lucide-react';
import {Helmet} from "react-helmet-async";
import getHelmetConfig from "../utils/helmetConfig";

const API_BASE_URL = 'https://texla.uz/api/v1';

const ContactPage = () => {
    const { t, language } = useLanguage();
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
    const bot = useBotProtection();
    const [submitted, setSubmitted] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        fetchAboutData();
    }, [language]);

    useEffect(() => {
        bot.reset();
    }, []);

    const fetchAboutData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/about/`);
            if (response.data && response.data.length > 0) {
                const about = response.data[0];
                setAboutData({
                    address_uz: about.address_uz,
                    address_ru: about.address_ru,
                    phone: about.phone,
                    email: about.email,
                    instagram: about.instagram,
                    telegram: about.telegram,
                    facebook: about.facebook,
                    map_address: about.map_address,
                });
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !formData.email || !formData.message) {
            return;
        }
        if (!bot.validate()) return;

        setSubmitLoading(true);
        try {
            await axios.post(`${API_BASE_URL}/contact/`, {
                full_name: formData.name,
                phone: formData.phone,
                email: formData.email,
                message: formData.message
            });

            setSubmitted(true);
            setFormData({ name: '', phone: '', email: '', message: '' });
            setTimeout(() => {
                setSubmitted(false);
            }, 3000);
        } catch (error) {
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.contact;


    return (
        <div className="bg-white">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <span className="text-gray-900">{t('header.contact')}</span>
                </div>
            </div>

            {/* Header */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('header.contact')}</h1>
                <p className="text-gray-600 text-lg">
                    {t('contact.description')}
                </p>
            </section>

            {/* Contact Information */}
            {aboutData && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        {/* Address */}
                        <div className="bg-blue-50 p-8 rounded-lg text-center">
                            <MapPin size={40} className="text-orange-600 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {t('about.address')}
                            </h3>
                            <p className="text-gray-600">
                                {language === 'uz' ? aboutData.address_uz : aboutData.address_ru}
                            </p>
                        </div>

                        {/* Phone */}
                        <div className="bg-green-50 p-8 rounded-lg text-center">
                            <Phone size={40} className="text-green-600 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {t('about.phone')}
                            </h3>
                            <p className="text-gray-600">
                                <a href={`tel:${aboutData.phone}`} className="hover:text-green-600 font-semibold">
                                    {aboutData.phone}
                                </a>
                            </p>
                        </div>

                        {/* Email */}
                        <div className="bg-purple-50 p-8 rounded-lg text-center">
                            <Mail size={40} className="text-purple-600 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {t('about.email')}
                            </h3>
                            <p className="text-gray-600">
                                <a href={`mailto:${aboutData.email}`} className="hover:text-purple-600 font-semibold">
                                    {aboutData.email}
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* Map */}
                    {aboutData.map_address && (
                        <div className="mb-12 rounded-lg overflow-hidden shadow-lg">
                            <iframe
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                src={`https://yandex.ru/map-widget/v1/?ll=${aboutData.map_address.replace(',', '%2C')}&z=15&l=map&pt=${aboutData.map_address.replace(',', '%2C')},pm2lbm`}
                                allowFullScreen=""
                            ></iframe>
                        </div>
                    )}

                    {/* Contact Form */}
                    <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-8">
                                {t('contact.sendMessage')}
                            </h2>

                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                        {t('modal.successTitle')}
                                    </h4>
                                    <p className="text-sm text-gray-600">{t('modal.successMessage')}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('modal.fullName')} <span className="text-red-500">{t('modal.required')}</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                                placeholder={t('modal.fullName')}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('modal.phone')} <span className="text-red-500">{t('modal.required')}</span>
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                                placeholder="+998 XX XXX XX XX"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('about.email')} <span className="text-red-500">{t('modal.required')}</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                            placeholder="example@mail.com"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.message')} <span className="text-red-500">{t('modal.required')}</span>
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            rows="6"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                                            placeholder={t('modal.message')}
                                            required
                                        />
                                    </div>

                                    <BotProtectionFields bot={bot} />

                                    <button
                                        type="submit"
                                        disabled={submitLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
                                    >
                                        {submitLoading ? (
                                            <>
                                                <Loader size={20} className="animate-spin" />
                                                {t('contact.sending')}
                                            </>
                                        ) : (
                                            <>
                                                <Send size={20} />
                                                {t('modal.send')}
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </section>
                </section>
            )}
        </div>
    );
};

export default ContactPage;